import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  CenterModal, ModalSize, Button, ButtonVariant,
} from "@repo/design-system/components/ui";
import { FormBuilder } from "@repo/design-system/components/forms";
import {
  setFieldSetting, FieldSettingType,
  setDropDownOptionField, setLazyDropdownFetchOptions, getFieldData, getValue,
} from "@repo/design-system/components/forms/utils";
import {
  useGetStudentDropdownQuery, useSaveProfileMutation,
  useLazyGetUniversityDropdownQuery, useLazyGetInstituteDropdownQuery,
} from "@gyandiksha/shared-ui/app/services";
import { ToastService } from "@/services";
import { buildAcademicRecordFormConfig, STREAM_OPTIONS } from "./config/AcademicRecordModal.config";
import { buildCollegeAcademicRecordFormConfig } from "./config/CollegeAcademicRecordModal.config";

const RECORD_TITLE = { ssc: "SSC Academic Record", hsc: "HSC Academic Record" };
// Backend program-level ids that identify which academic record is being saved.
export const PROGRAM_LEVEL_ID = {
  ssc: 1208, hsc: 1308, ug: 1, pg: 2, diploma: 3, phd: 4,
};
const SCHOOL_TYPES = ["ssc", "hsc"];
const INITIAL_CLEARED_SEARCH = { university: false, institute: false };
const SCHOOL_PROGRAM_LEVEL_IDS = [PROGRAM_LEVEL_ID.ssc, PROGRAM_LEVEL_ID.hsc];

const findOptionByLabel = (options, label) =>
  options?.find((option) => String(option.label).trim().toLowerCase() === String(label).trim().toLowerCase());

// Different sources key the same record differently — the dashboard's flattened record only ever
// carries a label (e.g. boardName: "CBSE ..."), while the register-academic-record response carries
// the id directly (boardId/passingYearId) alongside a label field whose name varies (boardName vs
// passingYearName). Matching on id first avoids depending on that label text lining up at all.
const findOptionByIdOrLabel = (options, id, label) =>
  (id != null ? options?.find((option) => String(option.value) === String(id)) : null)
  ?? (label ? findOptionByLabel(options, label) : null);

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";
// Backend sends 0 / 0.0 when a percentage / CGPA was never filled, so treat it as "no value".
const hasScore = (value) => hasValue(value) && Number(value) !== 0;

const EditAcademicRecordModal = ({
  isOpen, type, record, programLevelId, title, onClose, onSave,
}) => {
  const formRef = useRef();
  // The register-academic-record response doesn't reliably key its record by the selected
  // education type (e.g. programLevelId 1208 — SSc — has come back keyed "hsc"), so when the
  // caller gives us the programLevelId it actually requested (the "Add New Record" flow always
  // does), that's the trustworthy signal. Only the ssc/hsc edit-icon flow — which never passes
  // programLevelId — falls back to reading it off `type`.
  const isSchoolType = programLevelId != null
    ? SCHOOL_PROGRAM_LEVEL_IDS.includes(programLevelId)
    : SCHOOL_TYPES.includes(String(type).toLowerCase());
  // Stream is asked only for the HSC (12th) record
  const isHsc = programLevelId != null
    ? programLevelId === PROGRAM_LEVEL_ID.hsc
    : String(type).toLowerCase() === "hsc";
  const [formConfig, setFormConfig] = useState(buildAcademicRecordFormConfig);

  const { data: dropdownData, isFetching: isDropdownLoading } = useGetStudentDropdownQuery(undefined, {
    skip: !isOpen,
  });
  // Memoized so `?? []` doesn't hand out a new array reference on every render — this feeds the
  // form-rebuild effect's dependency array, and an unstable reference there re-triggers on every
  // keystroke, wiping out whatever the student just typed.
  const boardList = useMemo(() => dropdownData?.data?.board?.content ?? [], [dropdownData]);
  const yearList = useMemo(() => dropdownData?.data?.year?.content ?? [], [dropdownData]);

  // On edit, the record's current university / institute name is sent as the dropdown APIs' `search`
  const recordUniversityName = String(record?.mutable?.universityName ?? record?.immutable?.universityName ?? "").trim();
  const recordInstituteName = String(record?.mutable?.instituteName ?? record?.immutable?.instituteName ?? "").trim();

  // Once the student clears (✕) university / institute, only that dropdown's API is re-called with an empty search.
  const [clearedSearch, setClearedSearch] = useState(INITIAL_CLEARED_SEARCH);
  const universitySearch = clearedSearch.university ? "" : recordUniversityName;
  const instituteSearch = clearedSearch.institute ? "" : recordInstituteName;

  // University is paged + searchable the same way as institute (next page loads on scroll).
  const [getUniversity] = useLazyGetUniversityDropdownQuery();
  const fetchUniversityOptions = useCallback(async ({ page = 0, search, resolveValue } = {}) => {
    // Typed text wins; otherwise search by the record's university name (also used to resolve a bare id)
    const result = await getUniversity({ page, size: 10, search: search || universitySearch || resolveValue });
    const data = result?.data?.data ?? {};
    const items = (data.content ?? []).map((item) => ({ value: item.id, label: item.value }));
    if (resolveValue != null) {
      const match = items.find((item) => String(item.value) === String(resolveValue));
      return { items: match ? [match] : [], hasMore: false };
    }
    return {
      items,
      hasMore: (data.currentPage ?? 0) + 1 < (data.totalPages ?? 0),
    };
  }, [getUniversity, universitySearch]);

  // Institute runs into the thousands, so it's only ever paged through as the student searches.
  const [getInstitute] = useLazyGetInstituteDropdownQuery();
  const fetchInstituteOptions = useCallback(async ({ page = 0, search, resolveValue } = {}) => {
    // Best-effort fallback prefill path: the lazy select calls this with `resolveValue` when it
    // only has a bare id to work with. We normally avoid needing this (see the `instituteId`
    // initialState below, which already carries the label straight from the record), but it's
    // kept here so a raw id still resolves to *something* searchable if that ever isn't the case.
    // Typed text wins; otherwise search by the record's institute name (also used to resolve a bare id)
    const result = await getInstitute({ page, size: 10, search: search || instituteSearch || resolveValue });
    const data = result?.data?.data ?? {};
    const items = (data.content ?? []).map((item) => ({ value: item.id, label: item.value }));
    if (resolveValue != null) {
      const match = items.find((item) => String(item.value) === String(resolveValue));
      return { items: match ? [match] : [], hasMore: false };
    }
    return {
      items,
      hasMore: (data.currentPage ?? 0) + 1 < (data.totalPages ?? 0),
    };
  }, [getInstitute, instituteSearch]);

  const [saveProfile, { isLoading: isSaving }] = useSaveProfileMutation();

  // The backend partitions each record into fields the student can still edit ("mutable")
  // and fields it has locked ("immutable") — an immutable field is locked only when it actually has a value.
  const mutable = record?.mutable ?? {};
  const immutable = record?.immutable ?? {};
  const flatRecord = { ...immutable, ...mutable };

  useEffect(() => {
    if (!isOpen) return;

    if (isSchoolType) {
      const config = buildAcademicRecordFormConfig({ isHsc });
      setDropDownOptionField(boardList, "id", "value", config, "boardName");
      setDropDownOptionField(yearList, "id", "value", config, "passingYear");

      const boardOptions = getFieldData(config, "boardName")?.fieldSetting?.options ?? [];
      const yearOptions = getFieldData(config, "passingYear")?.fieldSetting?.options ?? [];
      const matchedBoard = findOptionByIdOrLabel(boardOptions, flatRecord?.boardId, flatRecord?.boardName);
      const matchedYear = findOptionByIdOrLabel(
        yearOptions, flatRecord?.passingYearId, flatRecord?.passingYear ?? flatRecord?.passingYearName,
      );

      config.initialState = {
        // Handed over as the resolved { value, label } option itself — not just its raw id —
        // so formData already carries the label even if the student never reopens the dropdown
        // (a bare scalar sits there as a primitive with no .label, so handleSave's label read
        // would silently come back empty until the field was actively re-selected).
        boardName: matchedBoard ?? "",
        schoolName: flatRecord?.schoolName ?? "",
        seatNo: flatRecord?.seatNo ?? "",
        passingYear: matchedYear ?? "",
        percentage: hasScore(flatRecord?.percentage) ? flatRecord.percentage : "",
        ...(isHsc ? { stream: findOptionByIdOrLabel(STREAM_OPTIONS, flatRecord?.streamId, flatRecord?.streamName) ?? "" } : {}),
      };
      // Lock only the fields the backend marked immutable — mutable fields stay editable regardless of value.
      if ("boardId" in immutable || "boardName" in immutable) setFieldSetting(config, "boardName", FieldSettingType.DISABLED, true);
      if ("schoolName" in immutable) setFieldSetting(config, "schoolName", FieldSettingType.DISABLED, true);
      if ("seatNo" in immutable) setFieldSetting(config, "seatNo", FieldSettingType.DISABLED, true);
      if ("passingYearId" in immutable || "passingYear" in immutable) setFieldSetting(config, "passingYear", FieldSettingType.DISABLED, true);
      if ("percentage" in immutable) setFieldSetting(config, "percentage", FieldSettingType.DISABLED, true);
      setFormConfig(config);
      return;
    }

    // College-type records (UG/PG/Diploma/PhD) — no board, seat number, or percentage;
    // university/institute/enrollment/passing year/CGPA instead.
    const config = buildCollegeAcademicRecordFormConfig();
    setDropDownOptionField(yearList, "id", "value", config, "passingYear");
    setLazyDropdownFetchOptions(config, "universityId", fetchUniversityOptions);
    setLazyDropdownFetchOptions(config, "instituteId", fetchInstituteOptions);

    const yearOptions = getFieldData(config, "passingYear")?.fieldSetting?.options ?? [];
    const matchedYear = findOptionByIdOrLabel(
      yearOptions, flatRecord?.passingYearId, flatRecord?.passingYear ?? flatRecord?.passingYearName,
    );

    // After a clear (✕) the dropdown options reload — keep what the student has in the form
    // instead of refilling it from the record.
    const currentFormData = clearedSearch.university || clearedSearch.institute
      ? formRef.current?.getFormData() ?? {} : {};

    config.initialState = {
      // Same as institute: { value, label } straight from the record (lazy select, filled by id),
      // or the bare id so the lazy select resolves the label itself.
      universityId: flatRecord?.universityId != null
        ? (flatRecord?.universityName
          ? { value: flatRecord.universityId, label: String(flatRecord.universityName).trim() }
          : flatRecord.universityId)
        : "",
      // Handed over as a resolved { value, label } object (the record already carries the
      // institute's name) when available. Otherwise, pass the bare id so the lazy select can
      // resolve the institute label by ID.
      instituteId: flatRecord?.instituteId != null
        ? (flatRecord?.instituteName
          ? { value: flatRecord.instituteId, label: flatRecord.instituteName }
          : flatRecord.instituteId)
        : "",
      seatNo: flatRecord?.seatNo ?? "",
      passingYear: matchedYear ?? "",
      cgpa: [flatRecord?.cgpa, flatRecord?.percentage].find(hasScore) ?? "",
      ...currentFormData,
    };
    // Same rule as the school form: immutable AND has a value → locked, otherwise editable.
    if (hasValue(immutable.universityId) || hasValue(immutable.universityName)) setFieldSetting(config, "universityId", FieldSettingType.DISABLED, true);
    if (hasValue(immutable.instituteId) || hasValue(immutable.instituteName)) setFieldSetting(config, "instituteId", FieldSettingType.DISABLED, true);
    if (hasValue(immutable.seatNo)) setFieldSetting(config, "seatNo", FieldSettingType.DISABLED, true);
    if (("passingYearId" in immutable || "passingYear" in immutable) && matchedYear) setFieldSetting(config, "passingYear", FieldSettingType.DISABLED, true);
    if (hasScore(immutable.cgpa) || hasScore(immutable.percentage)) setFieldSetting(config, "cgpa", FieldSettingType.DISABLED, true);
    setFormConfig(config);
  }, [isOpen, record, isSchoolType, isHsc, boardList, yearList, fetchUniversityOptions, fetchInstituteOptions]);

  // ✕ on university / institute → re-call that dropdown API with an empty search
  const handleColumnChange = useCallback((dataField, updatedData) => {
    if (dataField !== "universityId" && dataField !== "instituteId") return;
    if (getValue(updatedData?.[dataField])) return;
    setClearedSearch((prev) => ({
      ...prev,
      [dataField === "universityId" ? "university" : "institute"]: true,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setClearedSearch(INITIAL_CLEARED_SEARCH);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    const formData = formRef.current?.getValidatedFormData();
    if (!formData) return;

    const passingYearOption = formData.passingYear;
    const passingYearId = Number(getValue(passingYearOption));

    // Send every field on the form, not just the ones the student touched — programLevelId
    // always goes along too. A caller adding a brand-new record (any education type) passes its
    // own programLevelId; the ssc/hsc edit-icon flow relies on the fixed map instead.
    const payload = { programLevelId: programLevelId ?? PROGRAM_LEVEL_ID[type] };

    if (isSchoolType) {
      const boardOption = formData.boardName;
      payload.boardId = Number(getValue(boardOption));
      payload.boardName = boardOption?.label ?? "";
      payload.passingYearId = passingYearId;
      payload.passingYear = passingYearOption?.label ?? "";
      payload.percentage = Number(formData.percentage);
      payload.seatNo = formData.seatNo;
      payload.schoolName = formData.schoolName;
      if (isHsc) {
        const streamOption = formData.stream;
        payload.streamId = Number(getValue(streamOption));
        payload.streamName = streamOption?.label ?? "";
      }
    } else {
      const universityOption = formData.universityId;
      const instituteOption = formData.instituteId;
      payload.universityId = Number(getValue(universityOption));
      payload.universityName = universityOption?.label ?? "";
      payload.instituteId = Number(getValue(instituteOption));
      payload.instituteName = instituteOption?.label ?? "";
      payload.passingYearId = passingYearId;
      payload.passingYear = passingYearOption?.label ?? "";
      payload.cgpa = Number(formData.cgpa);
      // The dashboard read API keys this field "seatNo" (matching the school records' shape),
      // but the save endpoint expects it as "enrollmentNo" for college-type records.
      payload.enrollmentNo = formData.seatNo;
    }

    const result = await saveProfile(payload);
    if (result?.data) {
      if (result.data?.message) {
        ToastService.success({ description: result.data.message });
      }
      // Don't build a local display record — the parent refetches the dashboard and
      // renders whatever that response actually contains.
      setClearedSearch(INITIAL_CLEARED_SEARCH);
      onSave(type);
    }
  }, [type, programLevelId, isSchoolType, isHsc, onSave, saveProfile]);

  return (
    <CenterModal
      isOpen={isOpen}
      handleToggleModal={handleClose}
      modalTitle={title ?? RECORD_TITLE[type] ?? "Academic Record"}
      modelSizeClass={ModalSize.WD50}
      footerChildren={
        <>
          <Button variant={ButtonVariant.OUTLINE} leftIcon="material-symbols:close-rounded" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            leftIcon="material-symbols:save-rounded"
            onClick={handleSave}
            loading={isSaving}
            disabled={isDropdownLoading}
          >
            Save
          </Button>
        </>
      }
    >
      <FormBuilder ref={formRef} config={formConfig} onColumnChange={handleColumnChange} />
    </CenterModal>
  );
};

EditAcademicRecordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  type: PropTypes.string,
  record: PropTypes.object,
  programLevelId: PropTypes.number,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditAcademicRecordModal;
